import React, { useState } from 'react';
import toaster from '../Utils/toaster';
import { submitFeedback, FeedbackData } from '../services/feedbackApi';
import './FeedbackPanel.css';

interface FeedbackPanelProps {
    callHeaderId: string;
    onClose: () => void;
    feedbackSubmitted: boolean;
    onFeedbackSubmitted: () => void;
}

const FeedbackPanel: React.FC<FeedbackPanelProps> = ({
    callHeaderId,
    onClose,
    feedbackSubmitted,
    onFeedbackSubmitted,
}) => {
    const [rating, setRating] = useState(0);
    const [nps, setNps] = useState<number | null>(null);
    const [category, setCategory] = useState('');
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const categories = [
        'Call Widget',
        'Call Quality',
        'Client',
        'Platform Issue',
    ];

    const handleSubmit = async () => {
        if (rating === 0) {
            toaster('error', 'Please provide a rating for the client.');
            return;
        }
        // if (!category) {
        //     toaster('error', 'Please select a feedback concern.');
        //     return;
        // }
        if (nps === null) {
            toaster('error', 'Please provide an NPS score.');
            return;
        }

        try {
            setIsSubmitting(true);
            const feedbackData: FeedbackData = {
                rating,
                comment,
                category,
                nps,
            };

            // We pass the raw string ID here; API service will handle numeric extraction if needed
            await submitFeedback(callHeaderId, feedbackData);

            toaster('success', 'Thank you for your feedback!');
            onFeedbackSubmitted();
            setTimeout(() => {
                onClose();
            }, 1500); // Close after a short delay
        } catch (error) {
            console.error('Error submitting feedback:', error);
            toaster('error', 'Failed to submit feedback. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (feedbackSubmitted) {
        return (
            <div className="feedback-panel success-panel d-flex flex-column align-items-center justify-content-center h-100">
                <i className="bi bi-check-circle-fill text-success fs-1 mb-3"></i>
                <h5 className="text-center">Feedback Submitted!</h5>
            </div>
        );
    }

    return (
        <div className="feedback-panel">
            {/* Header with actions */}
            <div className='rating-call-header'>
                <div className='rating-call-title'>
                    <i className='bi bi-star-fill me-2' style={{ color: "white " }}></i>
                    <span>Rating & Feedback</span>
                </div>
            </div>

            <div className='feedback-contents'>
                {/* Client Rating */}
                <div className="mb-3">
                    <label className="form-label fw-bold">Please rate your experience with the client</label>
                    <div className="d-flex gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                            <i
                                key={star}
                                className={`bi bi-star-fill fs-3 cursor-pointer ${star <= rating ? 'text-warning' : 'text-muted'}`}
                                onClick={() => setRating(star)}
                                style={{ cursor: 'pointer' }}
                            ></i>
                        ))}
                    </div>
                </div>

                {/* Feedback Category */}
                <div className="mb-3">
                    <label className="form-label fw-bold">Please share your Feedback with us</label>
                    <select
                        className="form-select"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                    >
                        <option value="">Select Concern</option>
                        {categories.map((cat) => (
                            <option key={cat} value={cat}>
                                {cat}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Feedback Comment */}
                <div className="mb-3">
                    <textarea
                        className="form-control"
                        placeholder="Tell us more about your experience"
                        rows={3}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                    />
                </div>

                {/* NPS Rating */}
                <div className="mb-4">
                    <label className="form-label fw-bold text-center w-100">
                        How likely are you to recommend <br />
                        Ad Astra to a colleague or friend?
                    </label>
                    <div className="nps-container">
                        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                            <button
                                key={num}
                                type="button"
                                className={`btn btn-sm nps-btn ${nps === num ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => setNps(num)}
                                title={num.toString()}
                            >
                                {num}
                            </button>
                        ))}
                    </div>
                    <div className="d-flex justify-content-between mt-1 text-muted small">
                        <span>Not likely</span>
                        <span>Very likely</span>
                    </div>
                </div>

                {/* Actions */}
                <div className="d-flex justify-content-end gap-2">
                    {/* We might not want a 'Close' button if it's mandatory or tied to call end, 
              but usually good UX to have one or rely on parent close. 
              Here we just satisfy the requirement of saving. */}
                    <button
                        className="btn btn-warning rounded-pill w-100"
                        style={{ backgroundColor: '#e58831' }}
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Saving...
                            </>
                        ) : (
                            'Save'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FeedbackPanel;